import React, { Component } from 'react';
import Button from '@mui/material/Button';
import { Grid, Typography } from '@mui/material';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import GenerateToken from '../../Component/GenerateToken';
import useProjectStore from '../../ProjectCanvas/useProjectStore';

class SetToken extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasToken: this.props.hasToken,
      tokenRegenerated: false,
    };
  }

  TOKEN_INSTRUCTIONS_URL =
    'https://docs.workflow86.com/docs/components/webhook_import/#how-to-set-the-token';

  changeTokenStatus = (status, wasRegenerated = false) => {
    this.setState({ hasToken: status });
    this.props.changeTokenStatus(status);
    
    if (wasRegenerated && status) {
      this.setState({ tokenRegenerated: true });
    }
  };

  render() {
    const { hasToken, tokenRegenerated } = this.state;
    const isPublished = useProjectStore.getState().publishedVersion && useProjectStore.getState().publishedVersion > 0;

    return (
      <Grid container spacing={2}>
        {/* Status Banner */}
        <Grid item xs={12}>
          {tokenRegenerated ? (
            <div className="webhook-status-banner warning">
              <WarningAmberIcon className="status-banner-icon" />
              <div className="status-banner-content">
                <span className="status-banner-title">Token Regenerated - Republish Required</span>
                <span className="status-banner-subtitle">
                  You have regenerated the security token. The webhook will not accept requests with the new token until the workflow is republished.
                </span>
              </div>
            </div>
          ) : isPublished && hasToken ? (
            <div className="webhook-status-banner active">
              <CheckCircleIcon className="status-banner-icon" />
              <div className="status-banner-content">
                <span className="status-banner-title">Webhook Active</span>
                <span className="status-banner-subtitle">
                  This webhook endpoint is live and accepting incoming requests
                </span>
              </div>
            </div>
          ) : (
            <div className="webhook-status-banner inactive">
              <ErrorIcon className="status-banner-icon" />
              <div className="status-banner-content">
                <span className="status-banner-title">Webhook Inactive</span>
                <span className="status-banner-subtitle">
                  {!hasToken
                    ? 'Generate a security token and publish the workflow to activate this webhook'
                    : 'Publish the workflow to activate this webhook endpoint'}
                </span>
              </div>
            </div>
          )}
        </Grid>

        {/* Security Token Section */}
        <Grid item xs={12} className="webhook-section">
          <Typography className="webhook-section-label">Security Token</Typography>
          <Typography className="webhook-section-helper">
            Generate a token and include it in the request header (recommended) or as a URL parameter.{' '}
            <a
              href={this.TOKEN_INSTRUCTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1976d2', textDecoration: 'none' }}
            >
              Learn more
            </a>
          </Typography>

          <GenerateToken
            hasToken={status => {
              const wasRegenerated = this.state.hasToken && status;
              this.changeTokenStatus(status, wasRegenerated);
            }}
            tokenStatus={this.props.hasToken}
            name={this.props.ComponentName}
            component_id={this.props.component_id}
            isAlignCenter={false}
          />

          {/* Token Status Messages */}
          {tokenRegenerated && (
            <div className="webhook-token-warning">
              <WarningAmberIcon className="warning-icon" />
              <div className="warning-content">
                <span className="warning-title">Action Required: Republish Workflow</span>
                <span className="warning-text">
                  You have generated a new token. The webhook will reject requests using the old token. 
                  Republish this workflow and update your external system with the new token.
                </span>
              </div>
            </div>
          )}

          {!hasToken && (
            <div className="webhook-info-box">
              <InfoOutlinedIcon className="info-icon" />
              <span className="info-text">
                A security token is required to protect your webhook endpoint from unauthorized requests.
              </span>
            </div>
          )}

          {hasToken && !tokenRegenerated && (
            <div className="webhook-info-box">
              <InfoOutlinedIcon className="info-icon" />
              <span className="info-text">
                A token has been generated. If you've lost it, you can regenerate a new one - but remember to update your external system and republish the workflow.
              </span>
            </div>
          )}
        </Grid>

        {/* Instructions */}
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Once the token has been set up in your external system, click Finish to complete the setup.
          </Typography>
        </Grid>

        {/* Navigation Buttons */}
        <Grid item xs={12} style={{ display: 'flex', gap: '12px' }}>
          <Button
            onClick={() => this.props.onChangeState(4, 4)}
            variant="outlined"
          >
            Back
          </Button>
          <Button
            onClick={() => this.props.onChangeState(6, 6)}
            disabled={!hasToken}
            variant="contained"
            color="primary"
          >
            Finish
          </Button>
        </Grid>
      </Grid>
    );
  }
}

export default SetToken;
